import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { UpgradeRequestDialog } from "@/components/UpgradeRequest";
import { CaseManagement } from "@/components/CaseManagement";
import { ReferralSection } from "@/components/ReferralSection";
import CommunityBadge from "@/components/CommunityBadge";
import TrustSealWidget from "@/components/client/TrustSealWidget";
import ClientProgressDashboard from "@/components/client/ClientProgressDashboard";
import { 
  LayoutDashboard, Calendar, Target, TrendingUp, Award, 
  LogOut, Plus, CheckCircle, Clock, Zap, Heart, Lightbulb,
  ChevronRight, Flame, Video, ExternalLink, Users, Users2, DollarSign, CalendarPlus,
  MessageSquare, FileText, Search, Filter, BarChart3, Palette, Briefcase, FolderKanban, ClipboardList, FileDown, Timer, ClipboardCheck, BookOpen, ArrowUpCircle, HelpCircle, Rocket, Gift, Bell, Ticket, Mic, CreditCard, Trophy, Download, Shield
} from "lucide-react";

export const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showCasesDialog, setShowCasesDialog] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [consultantNotes, setConsultantNotes] = useState([]);
  const [expandedNote, setExpandedNote] = useState(null);
  const [noteReply, setNoteReply] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [consultantInfo, setConsultantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteSearch, setNoteSearch] = useState("");
  const [noteFilter, setNoteFilter] = useState("all");
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);
  const [showTrustSealDialog, setShowTrustSealDialog] = useState(false);

  useEffect(() => {
    if (!user?.onboarded && user?.role === "client") {
      navigate("/onboarding");
      return;
    }
    // Check if access is pending intake - redirect to intake form
    if (user?.access_status === "pending_intake" && user?.role === "client") {
      navigate("/intake");
      return;
    }
    // Check if access is pending approval
    if (user?.access_status === "pending_approval" && user?.role === "client") {
      // Stay on dashboard but show pending state
      setLoading(false);
      return;
    }
    fetchDashboardData();
    fetchConsultantInfo();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, logsRes, challengesRes, workshopsRes, meetingsRes, notesRes] = await Promise.all([
        authFetch(`${API}/engagement-stats`, {  }),
        authFetch(`${API}/weekly-logs`, {  }),
        authFetch(`${API}/challenges`, {  }),
        authFetch(`${API}/workshops`, {  }),
        authFetch(`${API}/meetings`, {  }),
        authFetch(`${API}/my-notes`, {  })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (logsRes.ok) setRecentLogs((await logsRes.json()).slice(0, 3));
      if (challengesRes.ok) setChallenges(await challengesRes.json());
      if (workshopsRes.ok) setWorkshops((await workshopsRes.json()).slice(0, 3));
      if (meetingsRes.ok) setMeetings(await meetingsRes.json());
      if (notesRes.ok) setConsultantNotes(await notesRes.json());
    } catch (_error) {
      console.error("Error fetching dashboard data:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultantInfo = async () => {
    try {
      // Fetch any consultant with calendar_link to show booking option
      const response = await authFetch(`${API}/workshops`, {  });
      if (response.ok) {
        const workshops = await response.json();
        // For now, we'll check if there's any meeting with a consultant calendar link
      }
    } catch (_error) {
      console.error("Error fetching consultant info:", _error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCompleteChallenge = async (challengeId) => {
    try {
      const response = await authFetch(`${API}/challenges/${challengeId}/complete`, {
        method: "PUT",
        
      });
      if (response.ok) {
        toast.success("Challenge completed! Great work!");
        fetchDashboardData();
      }
    } catch (_error) {
      toast.error("Failed to complete challenge");
    }
  };

  const handleReplyToNote = async (noteId) => {
    if (!noteReply.trim()) {
      toast.error("Please enter a response");
      return;
    }
    setSubmittingReply(true);
    try {
      const response = await authFetch(`${API}/my-notes/${noteId}/respond`, {
        method: "POST",
        body: JSON.stringify({ content: noteReply })
      });
      if (response.ok) {
        toast.success("Response sent to your consultant!");
        setNoteReply("");
        setExpandedNote(null);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send response");
      }
    } catch (_error) {
      toast.error("Failed to send response");
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDownloadAttachment = async (noteId, fileName) => {
    try {
      const response = await authFetch(`${API}/notes/${noteId}/attachment`, {});
      if (response.ok) {
        const data = await response.json();
        // Create download link
        const link = document.createElement('a');
        link.href = `data:${data.type};base64,${data.data}`;
        link.download = data.name || fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Document downloaded!");
      } else {
        toast.error("Failed to download attachment");
      }
    } catch (_error) {
      toast.error("Failed to download attachment");
    }
  };

  // Filter and search notes (must be before early returns to follow hooks rules)
  const filteredNotes = useMemo(() => {
    return consultantNotes.filter(note => {
      const matchesSearch = noteSearch === "" || 
        note.title?.toLowerCase().includes(noteSearch.toLowerCase()) ||
        note.content?.toLowerCase().includes(noteSearch.toLowerCase());
      const matchesFilter = noteFilter === "all" || note.category === noteFilter;
      return matchesSearch && matchesFilter;
    });
  }, [consultantNotes, noteSearch, noteFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#333333] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const pendingChallenges = challenges.filter(c => !c.completed);
  const completedChallenges = challenges.filter(c => c.completed);

  const getNoteCategoryColor = (category) => {
    const colors = {
      general: "bg-neutral-100 text-neutral-700",
      action_item: "bg-blue-100 text-blue-700",
      recommendation: "bg-[#39FF14]/10 text-[#39FF14]",
      insight: "bg-purple-100 text-purple-700"
    };
    return colors[category] || colors.general;
  };

  const noteCategories = [
    { value: "all", label: "All Notes" },
    { value: "general", label: "General" },
    { value: "action_item", label: "Action Items" },
    { value: "recommendation", label: "Recommendations" },
    { value: "insight", label: "Insights" }
  ];

  // Show pending approval state
  if (user?.access_status === "pending_approval" && user?.role === "client") {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4" data-testid="pending-approval-screen">
        <Card className="max-w-lg w-full border-amber-200 bg-amber-50/50">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-3">Onboarding Complete!</h1>
            <p className="text-neutral-600 mb-6">
              Thank you for completing your onboarding, <span className="font-medium">{user?.name}</span>. 
              Your account is now pending review by a consultant.
            </p>
            <div className="bg-white rounded-lg p-4 border border-amber-200 mb-6">
              <p className="text-sm text-amber-800">
                <strong>What happens next?</strong><br/>
                A consultant will review your information and approve your access. 
                You'll be able to access your dashboard and assigned programs once approved.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                <Clock className="w-4 h-4 mr-2" />
                Check Status
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="text-neutral-500"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tier-based access control
  const userTier = user?.tier || 'basic';
  const hasRapidAccess = user?.rapid_access || user?.programs?.includes('rapid') || userTier === 'premium_rapid';
  const hasPodcastAccess = user?.podcast_access || user?.programs?.includes('podcast_academy') || userTier === 'podcast_academy';
  const hasStoryBrandAccess = ['growth_strategic', 'growth_optimization', 'growth_scale'].includes(userTier);
  const hasScalingAccess = userTier === 'growth_scale';
  
  // Define what each tier can access
  const tierAccess = {
    basic: ['projects', 'project-intake', 'weekly-log', 'ideal-client', 'vision-goals', 'time-budget', 'custom-report', 'brand-guidelines', 'referrals', 'help'],
    growth_strategic: ['projects', 'project-intake', 'weekly-log', 'ideal-client', 'vision-goals', 'action-plans', 'sales', 'brand-guidelines', 'business-plan', 'sop-generator', 'engagements', 'referrals', 'help'],
    growth_optimization: ['projects', 'project-intake', 'weekly-log', 'ideal-client', 'vision-goals', 'action-plans', 'sales', 'brand-guidelines', 'business-plan', 'time-budget', 'sop-generator', 'storybrand', 'custom-report', 'engagements', 'referrals', 'help'],
    growth_scale: ['projects', 'project-intake', 'weekly-log', 'ideal-client', 'vision-goals', 'action-plans', 'sales', 'brand-guidelines', 'business-plan', 'time-budget', 'sop-generator', 'storybrand', 'custom-report', 'scaling', 'engagements', 'referrals', 'help'],
  };
  
  const currentAccess = tierAccess[userTier] || tierAccess.basic;
  const canAccess = (feature) => currentAccess.includes(feature);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="client-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#333333] text-white p-4 hidden lg:flex flex-col">
        <div className="flex items-center space-x-2 mb-6 flex-shrink-0">
          <div className="w-8 h-8 bg-white/10 rounded-md flex items-center justify-center">
            <span className="text-white font-bold text-sm">MB</span>
          </div>
          <span className="font-semibold">My Biz Consulting</span>
        </div>

        <nav className="sidebar-nav space-y-1 flex-1 overflow-y-auto pr-2 min-h-0">
          <Link 
            to="/dashboard"
            className="flex items-center space-x-3 px-3 py-2.5 rounded-lg bg-white/10 text-white"
          >
            <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Dashboard</span>
          </Link>
          
          {/* R.A.P.I.D. - Add-on, must be assigned */}
          {hasRapidAccess && (
            <Link 
              to="/rapid"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white bg-[#444444]/50 hover:bg-[#555555]/70 transition-colors border border-[#39FF14]/30"
              data-testid="nav-rapid"
            >
              <Zap className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">R.A.P.I.D.™ Program</span>
            </Link>
          )}
          
          {/* Podcast Academy - Add-on, must be assigned */}
          {hasPodcastAccess && (
            <Link 
              to="/podcast"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white bg-[#444444]/50 hover:bg-[#555555]/70 transition-colors border border-pink-500/30"
              data-testid="nav-podcast"
            >
              <Mic className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Podcast Academy</span>
            </Link>
          )}
          
          {/* Projects - All tiers */}
          {canAccess('projects') && (
            <Link 
              to="/projects"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-projects"
            >
              <FolderKanban className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">My Projects</span>
            </Link>
          )}
          
          {canAccess('project-intake') && (
            <Link 
              to="/project-intake"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-project-intake"
            >
              <ClipboardList className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Project Intake</span>
            </Link>
          )}
          
          {/* Weekly Log - All tiers */}
          {canAccess('weekly-log') && (
            <Link 
              to="/weekly-log"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-weekly-log"
            >
              <Calendar className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Weekly Log</span>
            </Link>
          )}
          
          {/* Ideal Client - All tiers */}
          {canAccess('ideal-client') && (
            <Link 
              to="/ideal-client"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-ideal-client"
            >
              <Users className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Ideal Client</span>
            </Link>
          )}
          
          {/* Vision & Goals - All tiers */}
          {canAccess('vision-goals') && (
            <Link 
              to="/vision-goals"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-vision-goals"
            >
              <DollarSign className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Vision & Goals</span>
            </Link>
          )}
          
          {/* Action Plans - Strategic Foundation+ */}
          {canAccess('action-plans') && (
            <Link 
              to="/action-plans"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-action-plans"
            >
              <Target className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Action Plans</span>
            </Link>
          )}
          
          {/* Sales Performance - Strategic Foundation+ */}
          {canAccess('sales') && (
            <Link 
              to="/sales"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-sales"
            >
              <BarChart3 className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Sales & Performance</span>
            </Link>
          )}
          
          {/* Brand Guidelines - Strategic Foundation+ */}
          {canAccess('brand-guidelines') && (
            <Link 
              to="/brand-guidelines"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-brand"
            >
              <Palette className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Brand Guidelines</span>
            </Link>
          )}
          
          {/* Business Plan - Strategic Foundation+ */}
          {canAccess('business-plan') && (
            <Link 
              to="/business-plan"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-business-plan"
            >
              <Briefcase className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Business Plan</span>
            </Link>
          )}
          
          {/* Time Budget - Basic + Growth Optimization+ */}
          {canAccess('time-budget') && (
            <Link 
              to="/time-budget"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-time-budget"
            >
              <Timer className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Time Budget</span>
            </Link>
          )}
          
          {/* SOP Generator - Strategic Foundation+ */}
          {canAccess('sop-generator') && (
            <Link 
              to="/sop-generator"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-sop-generator"
            >
              <ClipboardCheck className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">SOP Generator</span>
            </Link>
          )}
          
          {/* StoryBrand - Growth Optimization+ (add-on for Strategic Foundation) */}
          {(canAccess('storybrand') || hasStoryBrandAccess) && (
            <Link 
              to="/storybrand"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-storybrand"
            >
              <BookOpen className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">StoryBrand</span>
            </Link>
          )}

          {/* My Progress Dashboard */}
          <button
            onClick={() => setActiveSection('progress')}
            className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left ${activeSection === 'progress' ? 'bg-white/10 text-white' : 'text-neutral-300 hover:bg-white/5'}`}
            data-testid="nav-my-progress"
          >
            <TrendingUp className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">My Progress</span>
          </button>
          
          {/* Custom Report - Basic + Growth Optimization+ */}
          {canAccess('custom-report') && (
            <Link 
              to="/custom-report"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-custom-report"
            >
              <FileDown className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Custom Report</span>
            </Link>
          )}
          
          {/* Scaling Tools - Scale & Leadership only */}
          {hasScalingAccess && (
            <Link 
              to="/scaling"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white bg-[#444444]/50 hover:bg-[#555555]/70 transition-colors border border-purple-500/30"
              data-testid="nav-scaling"
            >
              <Rocket className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Scaling Tools</span>
            </Link>
          )}
          
          {/* Referrals - All tiers */}
          {canAccess('referrals') && (
            <Link 
              to="/referrals"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
              data-testid="nav-referrals"
            >
              <Gift className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Refer & Earn</span>
            </Link>
          )}
          
          {/* Business Engagements - 6-month program (Strategic+) */}
          {canAccess('engagements') && (
            <Link 
              to="/engagements"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white bg-[#444444]/50 hover:bg-[#555555]/70 transition-colors border border-emerald-500/30"
              data-testid="nav-engagements"
            >
              <TrendingUp className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <span className="truncate">Growth Engagements</span>
            </Link>
          )}
          
          {/* Community - All members */}
          <Link 
            to="/community"
            className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30 transition-colors border border-purple-500/30"
            data-testid="nav-community"
          >
            <Users className="w-5 h-5 text-purple-400 flex-shrink-0" />
            <span className="truncate">Community Hub</span>
          </Link>
          
          {/* Help & Docs - All tiers */}
          <Link 
            to="/help"
            className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-neutral-300 hover:bg-white/5 transition-colors"
            data-testid="nav-help"
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Help & Docs</span>
          </Link>
          
          {/* My Overview & Actions */}
          <div className="mt-3 pt-3 border-t border-white/10">
            <Link 
              to="/my-overview"
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg bg-[#444444]/50 text-white hover:bg-[#444444]/50 transition-colors"
              data-testid="nav-my-overview"
            >
              <FileDown className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">My Overview</span>
            </Link>
            
            {/* Badge & Seal - compact row */}
            <div className="flex gap-1.5 mt-2">
              <Button
                variant="ghost"
                className="flex-1 h-8 text-xs text-neutral-300 hover:text-white hover:bg-white/5"
                onClick={() => setShowBadgeDialog(true)}
                data-testid="my-badge-btn"
              >
                <Award className="w-3.5 h-3.5 mr-1" />
                Badge
              </Button>
              <Button
                variant="ghost"
                className="flex-1 h-8 text-xs text-neutral-300 hover:text-white hover:bg-white/5"
                onClick={() => setShowTrustSealDialog(true)}
                data-testid="trust-seal-btn"
              >
                <Shield className="w-3.5 h-3.5 mr-1" />
                Seal
              </Button>
            </div>
            
            {/* Upgrade Plan Button */}
            <Button
              variant="outline"
              className="w-full mt-2 border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10 hover:text-[#39FF14] h-9 text-sm"
              onClick={() => setShowUpgradeDialog(true)}
              data-testid="upgrade-plan-btn"
            >
              <ArrowUpCircle className="w-4 h-4 mr-2" />
              Upgrade
            </Button>
          </div>
        </nav>

        <div className="pt-3 border-t border-white/10 flex-shrink-0 mt-2">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
              ) : (
                <span className="text-base font-semibold">{user?.name?.charAt(0) || "U"}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-[#39FF14] truncate">{user?.email}</p>
            </div>
          </div>
          <Link to="/pricing" className="w-full">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-neutral-300 hover:text-white hover:bg-white/5 h-8 text-sm"
              data-testid="pricing-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Pricing & Plans
            </Button>
          </Link>
          <Link to="/subscription" className="w-full">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-neutral-300 hover:text-white hover:bg-white/5 h-8 text-sm"
              data-testid="subscription-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              My Subscription
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-neutral-300 hover:text-white hover:bg-white/5 h-8 text-sm"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
          
          {/* Contact Info */}
          <div className="mt-2 pt-2 border-t border-white/10">
            <p className="text-[10px] text-neutral-500 mb-1">Contact Us</p>
            <p className="text-[10px] text-neutral-400">215-443-1076</p>
            <p className="text-[10px] text-neutral-400">304 Baltimore Avenue</p>
            <p className="text-[10px] text-neutral-400">Folcroft, PA 19032</p>
          </div>
          
          {/* Security Trust Badge */}
          <div className="mt-2 pt-2 border-t border-white/10">
            <div className="bg-green-500/10 rounded-lg px-2.5 py-2 border border-green-500/20 flex items-center gap-2">
              <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="text-[10px] font-medium text-green-400">AES-256 Encrypted & Monitored</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#333333] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">MB</span>
            </div>
            <span className="font-semibold text-neutral-900">My Biz</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Welcome Banner */}
        <div className="bg-[#333333] rounded-2xl p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold mb-2">Welcome back, {user?.name?.split(" ")[0]}!</h1>
              <p className="text-neutral-300">Continue building momentum this week.</p>
            </div>
            <Button 
              onClick={() => navigate("/weekly-log")}
              className="mt-4 md:mt-0 bg-white text-emerald-900 hover:bg-neutral-100"
              data-testid="add-weekly-log-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Log This Week
            </Button>
          </div>
        </div>

        {/* Conditional Main Content */}
        {activeSection === 'progress' ? (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-neutral-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#2BC40E]" /> My Progress
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setActiveSection('dashboard')} className="text-neutral-500">
                Back to Dashboard
              </Button>
            </div>
            <ClientProgressDashboard />
          </div>
        ) : (
        <>

        {/* R.A.P.I.D.™ CTA Card - Only show if user has R.A.P.I.D. access */}
        {hasRapidAccess && (
          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 mb-8 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">R.A.P.I.D.™ Business Recalibration</h3>
                    <p className="text-sm text-neutral-600">
                      Your structured path to leadership clarity and execution alignment.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/rapid")}
                  className="mt-4 md:mt-0 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="rapid-cta-btn"
                >
                  Access R.A.P.I.D.™
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-neutral-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Flame className="w-5 h-5 text-amber-500" />
                <span className="text-xs font-mono text-neutral-500">Streak</span>
              </div>
              <p className="text-3xl font-semibold text-neutral-900">{stats?.streak || 0}</p>
              <p className="text-sm text-neutral-500">weeks</p>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-xs font-mono text-neutral-500">Tasks</span>
              </div>
              <p className="text-3xl font-semibold text-neutral-900">{stats?.total_tasks_completed || 0}</p>
              <p className="text-sm text-neutral-500">completed</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Award className="w-5 h-5 text-purple-500" />
                <span className="text-xs font-mono text-neutral-500">Wins</span>
              </div>
              <p className="text-3xl font-semibold text-neutral-900">{stats?.total_accomplishments || 0}</p>
              <p className="text-sm text-neutral-500">logged</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <span className="text-xs font-mono text-neutral-500">Momentum</span>
              </div>
              <p className="text-3xl font-semibold text-neutral-900">{stats?.avg_momentum || 0}</p>
              <p className="text-sm text-neutral-500">avg rating</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Progress */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-neutral-200">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-semibold">Recent Progress</CardTitle>
                <Link to="/weekly-log" className="text-sm text-[#39FF14] hover:text-emerald-800">
                  View all
                </Link>
              </CardHeader>
              <CardContent>
                {recentLogs.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                    <p className="text-neutral-500 mb-4">No weekly logs yet</p>
                    <Button 
                      onClick={() => navigate("/weekly-log")}
                      className="bg-[#333333] hover:bg-[#444444] text-white"
                    >
                      Create Your First Log
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentLogs.map((log) => (
                      <div key={log.log_id} className="p-4 bg-neutral-50 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-neutral-900">Week of {log.week_start}</span>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="flex items-center text-amber-600">
                              <Zap className="w-4 h-4 mr-1" /> {log.focus_rating}/10
                            </span>
                            <span className="flex items-center text-green-600">
                              <Heart className="w-4 h-4 mr-1" /> {log.energy_rating}/10
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-neutral-500 mb-1">Accomplishments</p>
                            <p className="font-medium text-neutral-900">{log.accomplishments?.length || 0}</p>
                          </div>
                          <div>
                            <p className="text-neutral-500 mb-1">Tasks Done</p>
                            <p className="font-medium text-neutral-900">{log.completed_tasks?.length || 0}</p>
                          </div>
                          <div>
                            <p className="text-neutral-500 mb-1">Self-Care</p>
                            <p className="font-medium text-neutral-900">{log.self_care_activities?.length || 0}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Cases */}
            <Card className="border-neutral-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowCasesDialog(true)}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold flex items-center justify-between">
                  <span className="flex items-center">
                    <Briefcase className="w-5 h-5 mr-2 text-emerald-600" />
                    My Cases
                  </span>
                  <ChevronRight className="w-5 h-5 text-neutral-400" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-neutral-500 text-sm">View and interact with your consulting cases, support tickets, and strategy sessions.</p>
                <Button 
                  variant="outline" 
                  className="mt-4 w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={(e) => { e.stopPropagation(); setShowCasesDialog(true); }}
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  View Cases
                </Button>
              </CardContent>
            </Card>

            {/* Upcoming Workshops */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent>
                {workshops.length === 0 ? (
                  <p className="text-neutral-500 text-center py-4">No upcoming events</p>
                ) : (
                  <div className="space-y-3">
                    {workshops.map((workshop) => (
                      <div key={workshop.workshop_id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-[#39FF14]/10 rounded-lg flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-[#39FF14]" />
                          </div>
                          <div>
                            <p className="font-medium text-neutral-900">{workshop.title}</p>
                            <p className="text-sm text-neutral-500">{workshop.date} at {workshop.time}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-[#39FF14] border-emerald-200">
                          {workshop.type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scheduled 1:1 Meetings */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold flex items-center">
                  <Video className="w-5 h-5 mr-2 text-blue-500" />
                  Scheduled Meetings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {meetings.length === 0 ? (
                  <p className="text-neutral-500 text-center py-4">No meetings scheduled</p>
                ) : (
                  <div className="space-y-3">
                    {meetings.slice(0, 5).map((meeting) => {
                      const status = meeting.status || "scheduled";
                      const statusColors = {
                        scheduled: "bg-blue-50 border-blue-100",
                        checked_in: "bg-emerald-50 border-emerald-100",
                        completed: "bg-green-50 border-green-200",
                        cancelled: "bg-red-50 border-red-100 opacity-60",
                      };
                      const badgeColors = {
                        scheduled: "text-blue-700 border-blue-200",
                        checked_in: "text-emerald-700 border-emerald-200",
                        completed: "text-green-700 border-green-200",
                        cancelled: "text-red-600 border-red-200",
                      };
                      return (
                        <div key={meeting.meeting_id} className={`p-4 rounded-lg border ${statusColors[status]}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-neutral-900">{meeting.title}</p>
                              <p className="text-sm text-neutral-500">{meeting.date} at {meeting.time}</p>
                            </div>
                            <div className="flex gap-1.5">
                              <Badge variant="outline" className={`text-xs ${badgeColors[status]}`}>
                                {status.replace("_", " ")}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-neutral-500 border-neutral-200">
                                {meeting.meeting_type}
                              </Badge>
                            </div>
                          </div>
                          {meeting.description && (
                            <p className="text-sm text-neutral-600 mb-2">{meeting.description}</p>
                          )}
                          {meeting.meeting_link && status !== "cancelled" && status !== "completed" && (
                            <a 
                              href={meeting.meeting_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Join Meeting
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Consultant Notes */}
            <Card className="border-neutral-200" data-testid="consultant-notes-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-emerald-500" />
                  Notes from Consultant
                  {consultantNotes.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {consultantNotes.length}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {consultantNotes.length === 0 ? (
                  <div className="text-center py-4">
                    <FileText className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
                    <p className="text-neutral-500 text-sm">No notes yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Search and Filter */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                        <Input
                          placeholder="Search notes..."
                          value={noteSearch}
                          onChange={(e) => setNoteSearch(e.target.value)}
                          className="pl-9 h-9 text-sm bg-white"
                          data-testid="notes-search-input"
                        />
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {noteCategories.map((cat) => (
                          <button
                            key={cat.value}
                            onClick={() => setNoteFilter(cat.value)}
                            className={`px-2 py-1 text-xs rounded-full transition-colors ${
                              noteFilter === cat.value
                                ? "bg-emerald-600 text-white"
                                : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                            }`}
                            data-testid={`filter-${cat.value}`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Notes List */}
                    {filteredNotes.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-neutral-500 text-sm">No notes match your search</p>
                        <button 
                          onClick={() => { setNoteSearch(""); setNoteFilter("all"); }}
                          className="text-emerald-600 text-sm mt-1 hover:underline"
                        >
                          Clear filters
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                        {filteredNotes.map((note) => (
                          <div key={note.note_id} className="p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-testid={`note-${note.note_id}`}>
                            <div className="flex items-start justify-between mb-2">
                              <p className="font-medium text-neutral-900 text-sm">{note.title}</p>
                              <Badge className={`text-xs ${getNoteCategoryColor(note.category)}`}>
                                {note.category?.replace("_", " ")}
                              </Badge>
                            </div>
                            <p className="text-sm text-neutral-600">{note.content}</p>
                            
                            {/* Attachment Display */}
                            {note.attachment && (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-800">
                                      {note.attachment.name}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleDownloadAttachment(note.note_id, note.attachment.name)}
                                    className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                                    data-testid={`download-attachment-${note.note_id}`}
                                  >
                                    <Download className="w-3 h-3" />
                                    Download
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            <p className="text-xs text-neutral-400 mt-2">
                              {new Date(note.created_at).toLocaleDateString()}
                              {note.consultant_name && ` • ${note.consultant_name}`}
                            </p>
                            
                            {/* Responses/Conversation Thread */}
                            {note.responses && note.responses.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-emerald-200 space-y-2">
                                <p className="text-xs font-medium text-emerald-700">Conversation ({note.responses.length})</p>
                                {note.responses.map((resp) => (
                                  <div 
                                    key={resp.response_id} 
                                    className={`p-2 rounded text-sm ${
                                      resp.author_role === "consultant" 
                                        ? "bg-blue-50 border-l-2 border-blue-400" 
                                        : "bg-white border-l-2 border-emerald-400"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-xs">{resp.author_name}</span>
                                      {resp.author_role === "consultant" && (
                                        <Badge className="bg-blue-100 text-blue-700 text-xs py-0">Consultant</Badge>
                                      )}
                                      <span className="text-xs text-neutral-400">
                                        {new Date(resp.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <p className="text-neutral-700">{resp.content}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Reply Section */}
                            <div className="mt-3 pt-2 border-t border-emerald-200">
                              {expandedNote === note.note_id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={noteReply}
                                    onChange={(e) => setNoteReply(e.target.value)}
                                    placeholder="Write your response..."
                                    className="w-full p-2 text-sm border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                                    rows={3}
                                    maxLength={2000}
                                    data-testid={`reply-textarea-${note.note_id}`}
                                  />
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-neutral-400">{noteReply.length}/2000</span>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => { setExpandedNote(null); setNoteReply(""); }}
                                        className="px-3 py-1 text-xs text-neutral-600 hover:bg-neutral-100 rounded"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleReplyToNote(note.note_id)}
                                        disabled={submittingReply || !noteReply.trim()}
                                        className="px-3 py-1 text-xs bg-[#39FF14] hover:bg-[#32E010] text-black font-medium rounded disabled:opacity-50"
                                        data-testid={`send-reply-${note.note_id}`}
                                      >
                                        {submittingReply ? "Sending..." : "Send Response"}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setExpandedNote(note.note_id)}
                                  className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                                  data-testid={`reply-btn-${note.note_id}`}
                                >
                                  <MessageSquare className="w-3 h-3" />
                                  Reply to this note
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Results summary */}
                    {(noteSearch || noteFilter !== "all") && filteredNotes.length > 0 && (
                      <p className="text-xs text-neutral-500 text-center">
                        Showing {filteredNotes.length} of {consultantNotes.length} notes
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Content */}
          <div className="space-y-6">
            {/* Weekly Challenges */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold flex items-center">
                  <Target className="w-5 h-5 mr-2 text-amber-500" />
                  Weekly Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingChallenges.length === 0 ? (
                  <p className="text-neutral-500 text-center py-4">No active challenges</p>
                ) : (
                  <div className="space-y-3">
                    {pendingChallenges.slice(0, 3).map((challenge) => (
                      <div key={challenge.challenge_id} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="font-medium text-neutral-900 mb-1">{challenge.title}</p>
                        <p className="text-sm text-neutral-600 mb-3">{challenge.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-amber-700">Due: {challenge.due_date}</span>
                          <Button 
                            size="sm" 
                            className="bg-amber-500 hover:bg-amber-600 h-8"
                            onClick={() => handleCompleteChallenge(challenge.challenge_id)}
                            data-testid={`complete-challenge-${challenge.challenge_id}`}
                          >
                            Complete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {completedChallenges.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <p className="text-sm text-neutral-500 mb-2">
                      {completedChallenges.length} challenge{completedChallenges.length > 1 ? "s" : ""} completed
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Energy Levels */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Your Averages</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-neutral-600">Focus</span>
                    <span className="font-mono text-neutral-900">{stats?.avg_focus || 0}/10</span>
                  </div>
                  <Progress value={(stats?.avg_focus || 0) * 10} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-neutral-600">Energy</span>
                    <span className="font-mono text-neutral-900">{stats?.avg_energy || 0}/10</span>
                  </div>
                  <Progress value={(stats?.avg_energy || 0) * 10} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-neutral-600">Momentum</span>
                    <span className="font-mono text-neutral-900">{stats?.avg_momentum || 0}/10</span>
                  </div>
                  <Progress value={(stats?.avg_momentum || 0) * 10} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* Quick Tier Info */}
            {!user?.tier && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">Unlock More Features</h3>
                  <p className="text-sm text-[#39FF14] mb-4">
                    Choose a plan to access sales tracking, brand tools, and more.
                  </p>
                  <Button 
                    onClick={() => navigate("/")}
                    className="w-full bg-[#333333] hover:bg-[#444444] text-white"
                  >
                    View Plans
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 px-2 py-2 z-50">
          <div className="flex items-center justify-around">
            <Link to="/dashboard" className="flex flex-col items-center text-[#39FF14]">
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-xs mt-1">Home</span>
            </Link>
            <Link to="/community" className="flex flex-col items-center text-neutral-400 hover:text-[#39FF14]">
              <Users2 className="w-5 h-5" />
              <span className="text-xs mt-1">Community</span>
            </Link>
            <Link to="/weekly-log" className="flex flex-col items-center text-neutral-400 hover:text-[#39FF14]">
              <Calendar className="w-5 h-5" />
              <span className="text-xs mt-1">Log</span>
            </Link>
            <Link to="/vision-goals" className="flex flex-col items-center text-neutral-400 hover:text-[#39FF14]">
              <Target className="w-5 h-5" />
              <span className="text-xs mt-1">Goals</span>
            </Link>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentTier={user?.tier}
      />

      {/* My Cases Dialog */}
      <Dialog open={showCasesDialog} onOpenChange={setShowCasesDialog}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-emerald-600" />
              My Cases
            </DialogTitle>
            <DialogDescription>
              View and interact with your consulting cases, support tickets, and strategy sessions
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            <CaseManagement user={user} isAdmin={false} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Community Badge Dialog */}
      <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              Your Community Badge
            </DialogTitle>
            <DialogDescription>
              Download and share your membership badge on social media!
            </DialogDescription>
          </DialogHeader>
          <CommunityBadge />
        </DialogContent>
      </Dialog>

      {/* Trust Seal Widget Dialog */}
      <Dialog open={showTrustSealDialog} onOpenChange={setShowTrustSealDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              Trust Seal Widget
            </DialogTitle>
            <DialogDescription>
              Add this security badge to your website to build trust with visitors
            </DialogDescription>
          </DialogHeader>
          <TrustSealWidget clientName={user?.name} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
